import Grid from '@mui/material/Grid';
import { formValidationSchema } from 'common/functions/form-validation-schema';
import { selectConditionTypeOptions, selectTitleStatusOptions } from 'common/hooks/useSelectOptions';
import { SelectBuildOption, selectOptionTools } from 'common/tools/select-option-tools';
import { useCarKeys } from 'data/useCarKeys';
import { useCarSpecs } from 'data/useCarSpecs';
import { Formik, FormikProps } from 'formik';
import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { newListingActions, newListingSelectors } from 'redux/new-listing.slice';
import { newListingMetaActions, newListingMetaSelectors } from 'redux/new-listing-meta.slice';
import { RootState } from 'redux/root-reducer';
import * as yup from 'yup';

import { StepProps } from './CreateListing';
import { ListingFormSelect, ListingFormTextField, ListingRow } from './ListingComponents';
import StepperFooter from './StepperFooter';

interface FormValues {
  year: string;
  make: string;
  model: string;
  trimId: string;
  mileage: string;
  conditionType: string;
  titleStatus: string;
}

const validationSchema = yup.object().shape({
  year: formValidationSchema.required,
  make: formValidationSchema.required,
  model: formValidationSchema.required,
  trimId: formValidationSchema.required,
  mileage: formValidationSchema.required,
  conditionType: formValidationSchema.required,
  titleStatus: formValidationSchema.required,
});

const initOption = { id: '', label: '' };

const ListingModelInfo = (props: StepProps) => {
  const dispatch = useDispatch();

  const { getMakes, getModels } = useCarKeys();
  const { getTrims, getTrimSpecs } = useCarSpecs();

  const [runOnce, runOnceSet] = React.useState(true);

  const year = useSelector((state: RootState) => newListingSelectors.year(state));
  const make = useSelector((state: RootState) => newListingSelectors.make(state));
  const model = useSelector((state: RootState) => newListingSelectors.model(state));
  const trimId = useSelector((state: RootState) => newListingSelectors.trimId(state));
  const mileage = useSelector((state: RootState) => newListingSelectors.mileage(state));
  const conditionType = useSelector((state: RootState) => newListingSelectors.conditionType(state));
  const titleStatus = useSelector((state: RootState) => newListingSelectors.titleStatus(state));

  const yearOptions = useSelector((state: RootState) => newListingMetaSelectors.yearOptions(state));
  const makeOptions = useSelector((state: RootState) => newListingMetaSelectors.makeOptions(state));
  const modelOptions = useSelector((state: RootState) => newListingMetaSelectors.modelOptions(state));
  const trimOptions = useSelector((state: RootState) => newListingMetaSelectors.trimOptions(state));

  const conditionTypeOptions = React.useMemo(() => selectConditionTypeOptions(SelectBuildOption.includeEmpty), []);
  const titleStatusOptions = React.useMemo(() => selectTitleStatusOptions(SelectBuildOption.includeEmpty), []);

  const updateMakeOptions = useCallback(
    async (year: number) => {
      const makes = await getMakes(+year);
      dispatch(newListingMetaActions.makeOptions(selectOptionTools.fromStrings(makes, true, initOption)));
    },
    [dispatch, getMakes],
  );

  const updateModelOptions = useCallback(
    async (year: number, make: string) => {
      const models = await getModels(+year, make);
      dispatch(newListingMetaActions.modelOptions(selectOptionTools.fromStrings(models, true, initOption)));
    },
    [dispatch, getModels],
  );

  const updateTrimOptions = useCallback(
    async (year: number, make: string, model: string) => {
      const trims = await getTrims(+year, make, model);
      dispatch(newListingMetaActions.trimOptions(selectOptionTools.fromObjects(trims, 'id', 'trim', true, initOption)));
    },
    [dispatch, getTrims],
  );

  useEffect(() => {
    if (runOnce) {
      runOnceSet(false);
      if (year !== 0) updateMakeOptions(year);
      if (make !== '') updateModelOptions(year, make);
      if (model !== '') updateTrimOptions(year, make, model);
      props.setValidFn(trimId !== '');
    }
  }, [runOnce, year, make, model, trimId, updateMakeOptions, updateModelOptions, updateTrimOptions, props]);

  const formAction = async (formValues: FormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    try {
      setSubmitting(false);
      if (trimId) props.handleNext();
    } catch (error: any) {}
  };

  const onValueChange = async (
    event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>,
    formik?: FormikProps<any>,
  ) => {
    formik?.handleChange(event);
    switch (event.target.id) {
      case 'mileage': {
        dispatch(newListingActions.mileage(+event.target.value));
        break;
      }
      case 'conditionType': {
        dispatch(newListingActions.conditionType(event.target.value));
        break;
      }
      case 'titleStatus': {
        dispatch(newListingActions.titleStatus(event.target.value));
        break;
      }
      case 'year': {
        const year = event.target.value;
        props.setValidFn(false);
        formik!.setFieldValue('make', '', false);
        formik!.setFieldValue('model', '', false);
        formik!.setFieldValue('trimId', '', false);
        dispatch(newListingActions.keySet({ year: +year }));
        updateMakeOptions(+year);
        break;
      }
      case 'make': {
        const make = event.target.value;
        props.setValidFn(false);
        formik!.setFieldValue('model', '', false);
        formik!.setFieldValue('trimId', '', false);
        dispatch(newListingActions.keySet({ year, make }));
        updateModelOptions(year, make);
        break;
      }
      case 'model': {
        const model = event.target.value;
        props.setValidFn(false);
        formik!.setFieldValue('trimId', '', false);
        dispatch(newListingActions.keySet({ year, make, model }));
        updateTrimOptions(year, make, model);
        break;
      }
      case 'trimId': {
        const trimId = event.target.value;
        dispatch(newListingActions.trimId(trimId));
        dispatch(newListingActions.trim(trimOptions.find(x => x.id === trimId)?.label || ''));

        const trimSpecs = await getTrimSpecs(trimId);
        dispatch(newListingActions.bodyType(trimSpecs.bodyType === 'unknown' ? '' : trimSpecs.bodyType));
        dispatch(newListingActions.cylinderType(trimSpecs.cylinderType === 'unknown' ? '' : trimSpecs.cylinderType));
        dispatch(newListingActions.doorType(trimSpecs.doorType === 'unknown' ? '' : trimSpecs.doorType));
        dispatch(
          newListingActions.drivetrainType(trimSpecs.drivetrainType === 'unknown' ? '' : trimSpecs.drivetrainType),
        );
        dispatch(newListingActions.fuelType(trimSpecs.fuelType === 'unknown' ? '' : trimSpecs.fuelType));
        dispatch(
          newListingActions.transmissionType(
            trimSpecs.transmissionType === 'unknown' ? '' : trimSpecs.transmissionType,
          ),
        );

        props.setValidFn(trimId !== '');
      }
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        year: year.toString(),
        make,
        model,
        trimId,
        mileage: mileage === 0 ? '' : mileage.toString(),
        conditionType,
        titleStatus,
      }}
      onSubmit={(values, { setSubmitting }) => formAction(values, setSubmitting)}
    >
      {formik => (
        <>
          <Grid container component="form" onSubmit={formik.handleSubmit}>
            <ListingRow label="Year" gridLR={[3, 9]}>
              <ListingFormSelect
                id="year"
                width={200}
                data={yearOptions}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onValueChange(event, formik)}
                formik={formik}
              />
            </ListingRow>
            <ListingRow label="Make" gridLR={[3, 9]}>
              <ListingFormSelect
                id="make"
                width={200}
                data={makeOptions}
                disabled={year === 0}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onValueChange(event, formik)}
                formik={formik}
              />
            </ListingRow>
            <ListingRow label="Model" gridLR={[3, 9]}>
              <ListingFormSelect
                id="model"
                width={200}
                data={modelOptions}
                disabled={make === ''}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onValueChange(event, formik)}
                formik={formik}
              />
            </ListingRow>
            <ListingRow label="Trim" gridLR={[3, 9]}>
              <ListingFormSelect
                id="trimId"
                width={250}
                data={trimOptions}
                disabled={model === ''}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onValueChange(event, formik)}
                formik={formik}
              />
            </ListingRow>
            <ListingRow label="Mileage" gridLR={[3, 9]}>
              <ListingFormTextField
                id="mileage"
                // label="Mileage"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onValueChange(event, formik)}
                formik={formik}
                disabled={!trimId}
              />
            </ListingRow>
            <ListingRow label="Condition" gridLR={[3, 9]}>
              <ListingFormSelect
                id="conditionType"
                data={conditionTypeOptions}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onValueChange(event, formik)}
                formik={formik}
                disabled={!trimId}
              />
            </ListingRow>
            <ListingRow label="Title Status" gridLR={[3, 9]}>
              <ListingFormSelect
                id="titleStatus"
                data={titleStatusOptions}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onValueChange(event, formik)}
                formik={formik}
                disabled={!trimId}
              />
            </ListingRow>
            <StepperFooter
              handleCancel={props.handleCancel}
              isFirst={props.isFirst}
              isLast={props.isLast}
              handleBack={props.handleBack}
            />
          </Grid>
        </>
      )}
    </Formik>
  );
};

export default ListingModelInfo;
